import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import type { AggregationResult } from "@/hooks/useAggregations";
import type { AggregationKind } from "@/hooks/table/types";

interface AggregationPillProps {
  result: AggregationResult;
  onSwitchKind?: (key: string, kind: AggregationKind) => void;
  onRemove?: (key: string) => void;
  /** Si true, renderiza el menú para cambiar el tipo. Default true si hay kinds alternativas. */
  switchable?: boolean;
}

const kindLabel: Record<AggregationKind, string> = {
  sum: "Σ",
  avg: "x̄",
  count: "#",
  min: "min",
  max: "max",
};

const kindName: Record<AggregationKind, string> = {
  sum: "Suma",
  avg: "Promedio",
  count: "Conteo",
  min: "Mínimo",
  max: "Máximo",
};

/**
 * Pill individual de una agregación. Si hay kinds alternativas disponibles,
 * abre un popover para elegir entre ellas. Muestra solo el símbolo de la
 * operación activa y el valor (no el label de la columna).
 */
export default function AggregationPill({
  result,
  onSwitchKind,
  onRemove,
  switchable = true,
}: AggregationPillProps) {
  const [open, setOpen] = useState(false);
  const hasAlternatives = result.kinds.length > 1 && !!onSwitchKind;
  const canSwitch = switchable && hasAlternatives;

  const pill = (
    <Badge variant="outline" className="cursor-pointer">
      <span className="text-[11px] font-semibold uppercase tracking-wide">
        {kindLabel[result.kind]}
      </span>
      <span className="font-semibold">{result.display}</span>
      {canSwitch && (
        <ChevronDown className="h-3 w-3 text-muted-foreground ml-0.5" />
      )}
    </Badge>
  );

  if (!canSwitch) {
    return onRemove ? (
      <span className="inline-flex items-center gap-0.5">
        {pill}
        <button
          type="button"
          onClick={() => onRemove(result.key)}
          className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Quitar agregación"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    ) : (
      pill
    );
  }

  return (
    <span className="inline-flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex cursor-pointer">
            {pill}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-40 p-1.5 z-[60]"
          align="start"
          side="top"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground px-2 py-1">
          Tipo de agregación
        </div>
        <Separator />
        <div className="pt-1">
          {result.kinds.map((k) => {
            const isActive = k === result.kind;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  onSwitchKind!(result.key, k);
                  setOpen(false);
                }}
                className={
                  "w-full flex items-center justify-between rounded-sm px-2 py-1 text-xs " +
                  "transition-colors hover:bg-accent hover:text-accent-foreground " +
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
                  (isActive
                    ? "bg-accent/60 text-accent-foreground font-medium"
                    : "text-foreground")
                }
              >
                <span className="inline-flex items-center gap-3.5">
                  <span className="text-[11px] font-semibold w-3.5 text-center">
                    {kindLabel[k]}
                  </span>
                  <span>{kindName[k]}</span>
                </span>
                {isActive && <Check className="h-3 w-3 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
      </Popover>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(result.key)}
          className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Quitar agregación"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
